import { useEffect, useMemo, useState } from "react";
import api, { API, BACKEND_URL } from "@/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Plus, Trash2, FileText, ChevronDown, MessageCircle, Pencil,
  User as UserIcon, Package as PkgIcon, Truck as TruckIcon, Wallet, CheckCircle2, Search,
} from "lucide-react";
import { toast } from "sonner";

const emptyBooking = {
  client_id: "", program_id: "", supplier_id: "",
  start_date: "", end_date: "", notes: "",
  total_amount: 0, deposit: 0, currency: "USD", payment_method: "",
};

const statusStyle = {
  paid: "bg-emerald-50 text-emerald-700",
  partial: "bg-amber-50 text-amber-700",
  unpaid: "bg-rose-50 text-rose-700",
};

const STEPS = [
  { key: "client", icon: UserIcon, label: "stepClient" },
  { key: "service", icon: PkgIcon, label: "stepService" },
  { key: "supplier", icon: TruckIcon, label: "stepSupplier" },
  { key: "details", icon: Wallet, label: "stepDetails" },
];

export default function Bookings() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBooking);
  const [step, setStep] = useState(0);
  const [q, setQ] = useState("");

  const load = async () => {
    const [b, c, p, pm, sp] = await Promise.all([
      api.get("/bookings"), api.get("/clients"), api.get("/programs"),
      api.get("/payment-methods"), api.get("/suppliers"),
    ]);
    setItems(b.data);
    setClients(c.data);
    setPrograms(p.data);
    setPaymentMethods(pm.data || []);
    setSuppliers(sp.data || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyBooking); setStep(0); setQ(""); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      client_id: b.client_id, program_id: b.program_id, supplier_id: b.supplier_id || "",
      start_date: b.start_date || "", end_date: b.end_date || "", notes: b.notes || "",
      total_amount: b.total_amount || 0, deposit: b.deposit || 0,
      currency: b.currency || b.program?.currency || "USD",
      payment_method: b.payment_method || "",
    });
    setStep(3); // jump straight to details when editing
    setOpen(true);
  };

  const onProgramSelect = (pid) => {
    const p = programs.find((x) => x.id === pid);
    const total = (p?.pricing || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    setForm((f) => ({
      ...f, program_id: pid,
      total_amount: f.total_amount || total,
      currency: p?.currency || f.currency || "USD",
    }));
  };

  const save = async () => {
    if (!form.client_id || !form.program_id) return toast.error(t("stepClient") + " / " + t("stepService"));
    try {
      const payload = {
        ...form,
        total_amount: parseFloat(form.total_amount || 0),
        deposit: parseFloat(form.deposit || 0),
      };
      if (editing) await api.put(`/bookings/${editing.id}`, payload);
      else await api.post("/bookings", payload);
      toast.success(t("saved"));
      setOpen(false);
      setForm(emptyBooking);
      setEditing(null);
      setStep(0);
      load();
    } catch { toast.error("Error"); }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/bookings/${id}`);
    load();
  };

  const openPdfInNewTab = (id, l) => {
    const token = localStorage.getItem("access_token") || "";
    window.open(`${API}/bookings/${id}/pdf?lang=${l}&token=${encodeURIComponent(token)}`, "_blank");
  };

  const shareWhatsApp = (b, l) => {
    const phone = (b.client?.phone || "").replace(/\D/g, "");
    if (!phone) return toast.error(t("phone") + "?");
    const shareUrl = `${BACKEND_URL}/api/public/pdf/${b.share_token}?lang=${l}`;
    const greeting = l === "ar"
      ? `مرحباً ${b.client?.name || ""}،\nمرفق ملف حجزك (${b.ref_no}):\n${shareUrl}`
      : `Hello ${b.client?.name || ""},\nHere is your booking (${b.ref_no}):\n${shareUrl}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(greeting)}`, "_blank");
  };

  const filteredClients = useMemo(() => {
    if (!q) return clients;
    const s = q.toLowerCase();
    return clients.filter((c) => [c.name, c.phone, c.email, c.passport].some((v) => (v || "").toLowerCase().includes(s)));
  }, [clients, q]);

  const filteredPrograms = useMemo(() => {
    if (!q) return programs;
    const s = q.toLowerCase();
    return programs.filter((p) => [p.name, p.type].some((v) => (v || "").toLowerCase().includes(s)));
  }, [programs, q]);

  const filteredSuppliers = useMemo(() => {
    if (!q) return suppliers;
    const s = q.toLowerCase();
    return suppliers.filter((sp) => [sp.name, sp.type, sp.city, sp.country].some((v) => (v || "").toLowerCase().includes(s)));
  }, [suppliers, q]);

  const selectedClient = clients.find((c) => c.id === form.client_id);
  const selectedProgram = programs.find((p) => p.id === form.program_id);
  const selectedSupplier = suppliers.find((s) => s.id === form.supplier_id);

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="uppercase-label">{t("bookings")}</div>
          <h1 className="display-font text-3xl font-bold text-[hsl(var(--primary))] mt-1">{t("bookings")}</h1>
          <div className="mt-3 divider-accent" />
        </div>
        <Button onClick={openNew} data-testid="booking-add-btn" className="btn-accent">
          <Plus size={16} className="me-2" /> {t("newBooking")}
        </Button>
      </div>

      <div className="dashboard-card">
        {items.length === 0 ? (
          <div className="text-center py-12 text-sm text-[hsl(var(--muted-foreground))]">{t("empty")}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("refNo")}</TableHead>
                <TableHead>{t("client")}</TableHead>
                <TableHead>{t("program")}</TableHead>
                <TableHead>{t("supplierName")}</TableHead>
                <TableHead>{t("totalAmount")}</TableHead>
                <TableHead>{t("balanceDue")}</TableHead>
                <TableHead>{t("paymentStatus")}</TableHead>
                <TableHead className="text-end">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((b) => {
                const cur = b.currency || b.program?.currency || "";
                return (
                  <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                    <TableCell className="font-mono text-xs">{b.ref_no}</TableCell>
                    <TableCell className="font-semibold">{b.client?.name || "—"}</TableCell>
                    <TableCell>{b.program?.name || "—"}</TableCell>
                    <TableCell className="text-sm">{b.supplier?.name || "—"}</TableCell>
                    <TableCell className="font-medium">{(b.total_amount || 0).toLocaleString()} {cur}</TableCell>
                    <TableCell>
                      <span className={"font-medium " + ((b.balance || 0) > 0 ? "text-rose-600" : "text-emerald-600")}>
                        {(b.balance || 0).toLocaleString()} {cur}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={"inline-flex items-center px-2 py-0.5 rounded-full text-xs " + (statusStyle[b.payment_status] || statusStyle.unpaid)} data-testid={`booking-status-${b.id}`}>
                        {t(b.payment_status === "paid" ? "paidFull" : b.payment_status === "partial" ? "partial" : "unpaid")}
                      </span>
                    </TableCell>
                    <TableCell className="text-end">
                      <div className="inline-flex items-center gap-1 rtl:flex-row-reverse">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" className="btn-accent" data-testid={`booking-pdf-${b.id}`}>
                              <FileText size={14} className="me-1" /> PDF <ChevronDown size={14} className="ms-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white">
                            <DropdownMenuItem onClick={() => openPdfInNewTab(b.id, "ar")}>العربية</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openPdfInNewTab(b.id, "en")}>English</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" data-testid={`booking-wa-${b.id}`}>
                              <MessageCircle size={14} className="me-1" /> WhatsApp
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white">
                            <DropdownMenuItem onClick={() => shareWhatsApp(b, "ar")}>العربية</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => shareWhatsApp(b, "en")}>English</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button size="icon" variant="ghost" onClick={() => openEdit(b)} data-testid={`booking-edit-${b.id}`}>
                          <Pencil size={15} />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => del(b.id)} className="text-red-600">
                          <Trash2 size={15} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Wizard */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? t("editBooking") : t("addBooking")}</DialogTitle>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center gap-1 sm:gap-2 pb-4 mb-2 border-b border-[hsl(var(--border))] overflow-x-auto">
            {STEPS.map((s, idx) => {
              const isActive = idx === step;
              const isDone = idx < step;
              return (
                <div key={s.key} className="flex items-center gap-2">
                  <button type="button" onClick={() => setStep(idx)}
                    data-testid={`step-${s.key}`}
                    className={"flex items-center gap-2 px-3 py-1.5 rounded-full text-xs sm:text-sm transition-colors " +
                      (isActive ? "bg-[hsl(var(--primary))] text-white font-semibold" :
                       isDone ? "bg-emerald-50 text-emerald-700 font-semibold" :
                       "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]")}>
                    {isDone ? <CheckCircle2 size={14} /> : <s.icon size={14} />}
                    <span className="whitespace-nowrap">{idx + 1}. {t(s.label)}</span>
                  </button>
                  {idx < STEPS.length - 1 && <ChevronDown size={14} className="rtl:rotate-90 -rotate-90 text-[hsl(var(--muted-foreground))]" />}
                </div>
              );
            })}
          </div>

          {/* Step 1: Client */}
          {step === 0 && (
            <div className="space-y-3">
              <SearchBar q={q} setQ={setQ} placeholder={t("searchClient")} testid="wizard-client-search" />
              <div className="max-h-72 overflow-y-auto rounded-lg border border-[hsl(var(--border))]">
                {filteredClients.length === 0 ? (
                  <div className="text-center py-10 text-sm text-[hsl(var(--muted-foreground))]">{t("empty")}</div>
                ) : (
                  <ul>
                    {filteredClients.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, client_id: c.id })}
                          data-testid={`wizard-client-${c.id}`}
                          className={"w-full text-start p-3 hover:bg-[hsl(var(--secondary))] transition-colors border-b border-[hsl(var(--border))] last:border-b-0 " +
                            (form.client_id === c.id ? "bg-[hsl(var(--primary))]/5 border-r-4 rtl:border-r-0 rtl:border-l-4 border-[hsl(var(--accent))]" : "")}>
                          <div className="font-semibold text-[hsl(var(--primary))]">{c.name}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                            {[c.phone, c.email, c.nationality].filter(Boolean).join(" · ")}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Service (Program) */}
          {step === 1 && (
            <div className="space-y-3">
              <SearchBar q={q} setQ={setQ} placeholder={t("searchProgram")} testid="wizard-program-search" />
              <div className="max-h-72 overflow-y-auto rounded-lg border border-[hsl(var(--border))]">
                {filteredPrograms.length === 0 ? (
                  <div className="text-center py-10 text-sm text-[hsl(var(--muted-foreground))]">{t("empty")}</div>
                ) : (
                  <ul>
                    {filteredPrograms.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => onProgramSelect(p.id)}
                          data-testid={`wizard-program-${p.id}`}
                          className={"w-full text-start p-3 hover:bg-[hsl(var(--secondary))] transition-colors border-b border-[hsl(var(--border))] last:border-b-0 " +
                            (form.program_id === p.id ? "bg-[hsl(var(--primary))]/5 border-r-4 rtl:border-r-0 rtl:border-l-4 border-[hsl(var(--accent))]" : "")}>
                          <div className="flex items-center justify-between">
                            <div className="font-semibold text-[hsl(var(--primary))]">{p.name}</div>
                            <span className="text-[10px] uppercase tracking-wider bg-[hsl(var(--secondary))] px-2 py-0.5 rounded text-[hsl(var(--muted-foreground))]">
                              {t(p.type === "full_package" ? "fullPackage" : p.type === "flight_hotel" ? "flightHotel" : p.type === "flight_only" ? "flightOnly" : "custom")}
                            </span>
                          </div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                            {p.duration_days} {t("day")} · {p.currency} · {(p.pricing || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0).toLocaleString()}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Supplier (optional) */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <SearchBar q={q} setQ={setQ} placeholder={t("searchSupplier")} testid="wizard-supplier-search" />
                <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, supplier_id: "" })} className="whitespace-nowrap" data-testid="wizard-supplier-skip">
                  {t("skipStep")}
                </Button>
              </div>
              <div className="max-h-72 overflow-y-auto rounded-lg border border-[hsl(var(--border))]">
                {filteredSuppliers.length === 0 ? (
                  <div className="text-center py-10 text-sm text-[hsl(var(--muted-foreground))]">{t("empty")}</div>
                ) : (
                  <ul>
                    {filteredSuppliers.map((s) => (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, supplier_id: s.id })}
                          data-testid={`wizard-supplier-${s.id}`}
                          className={"w-full text-start p-3 hover:bg-[hsl(var(--secondary))] transition-colors border-b border-[hsl(var(--border))] last:border-b-0 " +
                            (form.supplier_id === s.id ? "bg-[hsl(var(--primary))]/5 border-r-4 rtl:border-r-0 rtl:border-l-4 border-[hsl(var(--accent))]" : "")}>
                          <div className="font-semibold text-[hsl(var(--primary))]">{s.name}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                            {[t({
                              airline: "typeAirline", hotel: "typeHotel", visa: "typeVisa",
                              transport: "typeTransport", ground_service: "typeGroundService",
                            }[s.type] || "typeOther"), s.city, s.country].filter(Boolean).join(" · ")}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Details */}
          {step === 3 && (
            <div className="space-y-4">
              {/* Summary chips */}
              <div className="flex flex-wrap gap-2 text-xs">
                <Chip icon={UserIcon} label={selectedClient?.name || t("stepClient")} />
                <Chip icon={PkgIcon} label={selectedProgram?.name || t("stepService")} />
                {selectedSupplier && <Chip icon={TruckIcon} label={selectedSupplier.name} />}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={t("startDate")}><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="wizard-start-date" /></Field>
                <Field label={t("endDate")}><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="wizard-end-date" /></Field>
                <Field label={t("totalAmount")}><Input type="number" min={0} step="0.01" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} data-testid="booking-total-input" /></Field>
                <Field label={t("deposit")}><Input type="number" min={0} step="0.01" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} data-testid="booking-deposit-input" /></Field>
                <Field label={t("currency")}><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></Field>
                <Field label={t("paymentMethod")}>
                  <Select value={form.payment_method || "none"} onValueChange={(v) => setForm({ ...form, payment_method: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="booking-pm-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="none">—</SelectItem>
                      {paymentMethods.filter((p) => p.active).map((p) => (
                        <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label={t("notes")}><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex items-center gap-2 flex-wrap">
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <div className="flex-1" />
            {step > 0 && (
              <Button variant="outline" onClick={() => { setStep(step - 1); setQ(""); }} data-testid="wizard-back">
                {t("back")}
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button
                onClick={() => {
                  if (step === 0 && !form.client_id) return toast.error(t("stepClient"));
                  if (step === 1 && !form.program_id) return toast.error(t("stepService"));
                  setQ("");
                  setStep(step + 1);
                }}
                disabled={(step === 0 && !form.client_id) || (step === 1 && !form.program_id)}
                data-testid="wizard-next"
                className="bg-[hsl(var(--primary))] hover:opacity-90 text-white"
              >
                {t("next")}
              </Button>
            ) : (
              <Button onClick={save} data-testid="booking-save-btn" className="bg-[hsl(var(--primary))] hover:opacity-90 text-white">
                <CheckCircle2 size={15} className="me-2" />
                {editing ? t("save") : t("createBooking")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase-label">{label}</Label>
    {children}
  </div>
);

const Chip = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--secondary))] text-[hsl(var(--primary))] font-semibold">
    <Icon size={12} />
    {label}
  </span>
);

const SearchBar = ({ q, setQ, placeholder, testid }) => (
  <div className="relative flex-1">
    <Search className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className="ps-9" data-testid={testid} />
  </div>
);
